import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Article, ArrowRight, CalendarBlank } from "@phosphor-icons/react";

export function BlogList() {
  usePageMeta({ title: "Journal", path: "/blog", description: "Maintenance tips, how-tos and lessons from real jobs across Albury & Wodonga." });
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/public/blog").then(r => setPosts(r.data)).catch(()=>{}); }, []);
  return (
    <div data-testid="blog-page">
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 min-h-[60vh] flex items-center">
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
          <div className="eyebrow">Maintenance journal</div>
          <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">Tips, how-tos, and what we've learned.</h1>
        </div>
      </section>
      <section data-nav-theme="light" className="max-w-5xl mx-auto px-5 md:px-8 py-16">
        {posts.length === 0 ? (
          <div className="text-center text-slate-500 py-10">
            <Article size={36} className="mx-auto text-slate-300"/>
            <p className="mt-3">No articles yet — check back soon.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(p => (
              <Link key={p.id} to={`/blog/${p.id}`} data-testid={`blog-${p.id}`} className="block bg-white border border-slate-200 hover:border-navy p-6 rounded-sm transition-colors group">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CalendarBlank size={12} />{new Date(p.created_at).toLocaleDateString()}
                </div>
                <h2 className="font-display text-3xl tracking-wide text-navy mt-2 group-hover:text-navy transition-colors">{p.topic}</h2>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{(p.content || "").replace(/```meta[\s\S]*?```/, "").replace(/^#.*$/gm, "").slice(0, 200)}…</p>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-navy group-hover:text-navy">Read article <ArrowRight size={14}/></div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function BlogDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/public/blog/${id}`).then(r => setPost(r.data)).catch(() => setNotFound(true)); }, [id]);
  usePageMeta(
    post
      ? { title: post.topic, path: `/blog/${post.slug || post.id}`, description: (post.content || "").replace(/```meta[\s\S]*?```/, "").trim().slice(0, 160) }
      : { title: "Journal", path: "/blog" }
  );
  if (notFound) return <div className="max-w-3xl mx-auto px-5 pt-32 pb-20 text-center"><h2 className="font-display text-3xl tracking-wide text-navy">Article not found</h2><Link to="/blog" className="mt-3 inline-block text-navy">← Back to blog</Link></div>;
  if (!post) return <div className="text-center text-slate-400 pt-32 pb-20">Loading…</div>;
  return (
    <div data-testid="blog-detail">
      <section data-nav-theme="light" className="max-w-3xl mx-auto px-5 md:px-8 pt-32 pb-16">
        <Link to="/blog" className="text-xs text-navy font-semibold uppercase tracking-wider">← Maintenance journal</Link>
        <h1 className="font-display display-lg tracking-wide text-navy mt-3 leading-tight">{post.topic}</h1>
        <div className="text-xs text-slate-500 mt-3">{new Date(post.created_at).toLocaleDateString()}</div>
        <article className="prose prose-slate mt-8 max-w-none">
          <pre className="whitespace-pre-wrap font-sans text-base text-slate-700 leading-relaxed">{post.content.replace(/```meta[\s\S]*?```/, "").trim()}</pre>
        </article>
        <div className="mt-10 p-6 bg-navy text-white rounded-sm">
          <h3 className="font-display text-2xl tracking-wide">Need a hand with this around your place?</h3>
          <p className="text-slate-300 text-sm mt-1">We're a quick call away.</p>
          <Link to="/quote"><Button className="pill-navy mt-4">Get a free quote</Button></Link>
        </div>
      </section>
    </div>
  );
}
